import type { Dispatch, SetStateAction } from 'react';
import type { MultiValue, SingleValue } from 'react-select';

import type { UserOverrides } from '@revelio/auth';
import { Tab } from '@revelio/data-access';
import { Button, FormField, Select, Switch } from '@revelio/ui';

import { FieldGroup } from '../../../../shared/ui/admin/groups/field/field-group';
import type {
  PipelineSelectOption,
  ReportSelectOption,
} from '../../client-form';
import fieldStyles from '../../user-form.module.css';
import { serializeOptions } from '../../utils/helpers';
import type { SelectOption } from './impersonationModal.utils';
import styles from './impersonationOverrides.module.css';

interface ImpersonationClientOverridesFormProps {
  customOverrides: UserOverrides;
  setCustomOverrides: Dispatch<SetStateAction<UserOverrides>>;
  onReset: () => void;
  clientSelectOptions: SelectOption<string>[];
  selectedClientOption: SelectOption<string> | undefined;
  onSelectClientGroup: (clientName: string | undefined) => void;
  tabSelectOptions: SelectOption<Tab>[];
  selectedTabOptions: SelectOption<Tab>[];
  pipelineOptions: PipelineSelectOption[];
  selectedPipelineOptions: PipelineSelectOption[];
  reportOptions: ReportSelectOption[];
  selectedReportOptions: ReportSelectOption[];
}

export function ImpersonationClientOverridesForm({
  customOverrides,
  setCustomOverrides,
  onReset,
  clientSelectOptions,
  selectedClientOption,
  onSelectClientGroup,
  tabSelectOptions,
  selectedTabOptions,
  pipelineOptions,
  selectedPipelineOptions,
  reportOptions,
  selectedReportOptions,
}: ImpersonationClientOverridesFormProps) {
  const addableReportOptions = reportOptions.filter(
    (option) =>
      !option.preventAdditions ||
      selectedReportOptions.some((selected) => selected.label === option.label)
  );
  const hasReportsTab = customOverrides.tabs?.includes(Tab.Reports) ?? false;
  const hasDataBuilderTab =
    customOverrides.tabs?.includes(Tab.Deliverables) ?? false;

  return (
    <FieldGroup
      title="Client Group Settings"
      description="Overrides applied to the impersonated client group."
      action={
        <Button
          size="xs"
          variant="ghost"
          color="destructive"
          onClick={onReset}
          data-testid="reset-client-overrides-button"
        >
          Reset Client Overrides
        </Button>
      }
    >
      <div className={`${fieldStyles.fields} ${styles.column}`}>
        <FormField
          label="Client Group"
          htmlFor="impersonation-client-group-override"
          className={fieldStyles.row}
        >
          <Select<SelectOption<string>, false>
            inputId="impersonation-client-group-override"
            options={clientSelectOptions}
            value={selectedClientOption ?? null}
            placeholder="Select client group..."
            onChange={(selected: SingleValue<SelectOption<string>>) =>
              onSelectClientGroup(selected?.value)
            }
            isClearable={true}
            data-testid="client-group-select"
          />
        </FormField>

        <FormField
          label="Tabs"
          htmlFor="impersonation-tabs-override"
          className={styles.stackedRow}
        >
          <Select<SelectOption<Tab>, true>
            inputId="impersonation-tabs-override"
            isMulti
            options={tabSelectOptions}
            value={selectedTabOptions}
            placeholder="Select tabs..."
            onChange={(selected: MultiValue<SelectOption<Tab>>) => {
              const selectedTabs = selected.map((option) => option.value);
              setCustomOverrides((prev) => ({
                ...prev,
                tabs: selectedTabs.length ? selectedTabs : undefined,
              }));
            }}
            isClearable={true}
          />
        </FormField>

        {hasReportsTab && (
          <div>
            <span className={styles.subHeading}>
              Report Builder Configuration
            </span>
            <FormField
              label="Report Types"
              htmlFor="impersonation-report-types-override"
              className={styles.stackedRow}
            >
              <Select<ReportSelectOption, true>
                inputId="impersonation-report-types-override"
                isMulti
                options={addableReportOptions}
                value={selectedReportOptions}
                placeholder="Select report types..."
                onChange={(selected: MultiValue<ReportSelectOption>) => {
                  const { reportTypes } = serializeOptions(
                    selected.map((option) => ({
                      label: option.label,
                      value: option.value,
                    }))
                  );
                  setCustomOverrides((prev) => ({
                    ...prev,
                    reportTypes: reportTypes.length ? reportTypes : undefined,
                  }));
                }}
                isDisabled={!addableReportOptions.length}
                isClearable={
                  !addableReportOptions.some((option) => option.isFixed)
                }
                noOptionsMessage={() => 'No report types'}
              />
            </FormField>
          </div>
        )}

        {hasDataBuilderTab && (
          <div>
            <span className={styles.subHeading}>
              Data Builder Configuration
            </span>
            <FormField
              label="Subscribed Datasets"
              htmlFor="impersonation-datasets-override"
              className={styles.stackedRow}
            >
              <Select<PipelineSelectOption, true>
                inputId="impersonation-datasets-override"
                isMulti
                options={pipelineOptions}
                value={selectedPipelineOptions}
                placeholder="Select datasets..."
                onChange={(selected: MultiValue<PipelineSelectOption>) => {
                  const { pipelineTypes, postingSources } = serializeOptions(
                    selected.map((option) => ({
                      label: option.label,
                      value: option.value,
                    }))
                  );
                  setCustomOverrides((prev) => ({
                    ...prev,
                    pipelineTypes: pipelineTypes.length
                      ? pipelineTypes
                      : undefined,
                    postingSources: postingSources.length
                      ? postingSources
                      : undefined,
                  }));
                }}
                isClearable={true}
                noOptionsMessage={() => 'No datasets'}
              />
            </FormField>
          </div>
        )}

        <FormField
          label="Website Postings"
          className={`${fieldStyles.row} ${fieldStyles.endRow} ${fieldStyles.bareSwitch} ${styles.switchRow}`}
        >
          <Switch
            size="sm"
            inactiveLabel="Off"
            label="On"
            checked={customOverrides.linkupPostings ?? false}
            onChange={(event) =>
              setCustomOverrides((prev) => ({
                ...prev,
                linkupPostings: event.target.checked,
              }))
            }
            data-testid="linkup-postings-toggle"
          />
        </FormField>
      </div>
    </FieldGroup>
  );
}
